import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    HomeCloseNotificationAction,
    HomeGetApplicationMessagesListAction,
    HomeGetNotificationsListAction,
    HomeSetApplicationMessageAction,
    HomeSetNotificationAction,
    HomeSetSubNotificationTypeAction
} from 'src/app/_shared/stores/home/home.actions';
import {
    ListeHomeNotification,
    MessageAppliDto,
    MutationGetListeHomeNotificationsArgs,
    MutationGetListeMessageAppliGeneriqueArgs
} from 'src/graphql/generated-types';

export enum BeneficiaryNotificationsTypes {
    UNVALIDATED_SOCIAL_CRITERIA = 'UNVALIDATED_SOCIAL_CRITERIA',
    UNVALIDATED_PARTNER = 'UNVALIDATED_PARTNER',
    UNVALIDATED_CHILDREN = 'UNVALIDATED_CHILDREN',
    UNVALIDATED_EXTERNS = 'UNVALIDATED_EXTERNS',
    UNVALIDATED_VOUCHERS = 'UNVALIDATED_VOUCHERS'
}

export enum WebAdministrationNotificationsTypes {
    UNPUBLISHED_ARTICLES = 'UNPUBLISHED_ARTICLES',
    UNMODERATED_COMMENTS = 'UNMODERATED_COMMENTS',
    UNVALIDATED_ADVERTISEMENTS = 'UNVALIDATED_ADVERTISEMENTS',
    UNVALIDATED_FILES = 'UNVALIDATED_FILES',
    UNVALIDATED_FOLDERS = 'UNVALIDATED_FOLDERS'
}

export enum ActivityNotificationsTypes {
    RECEIVABLE_SUPPLIERS_ORDERS = 'RECEIVABLE_SUPPLIERS_ORDERS',
    PAYABLE_SUPPLIERS_INVOICES = 'PAYABLE_SUPPLIERS_INVOICES',
    DELIVERABLE_CLIENTS_ORDERS = 'DELIVERABLE_CLIENTS_ORDERS',
    PENDING_CLIENTS_ORDERS = 'PENDING_CLIENTS_ORDERS',
    INVOICABLE_CLIENTS_ORDERS = 'INVOICABLE_CLIENTS_ORDERS',
    PAYABLE_CLIENTS_INVOICES = 'PAYABLE_CLIENTS_INVOICES',
    STOCK_SHORTAGE_ARTICLES = 'STOCK_SHORTAGE_ARTICLES',

    // PRESTATIONS
    GIFTS_DELIVERABLE = 'GIFTS_DELIVERABLE',
    PENDING_GIFTS_RECORDS = 'PENDING_GIFTS_RECORDS',
    PENDING_PART_A_CHARGE_RECORDS = 'PENDING_PART_A_CHARGE_RECORDS',
    PENDING_REFUND_RECORDS = 'PENDING_REFUND_RECORDS',
    SUPPLIER_PART_A_CHARGE_RECORDS_INVOICEABLE = 'SUPPLIER_PART_A_CHARGE_RECORDS_INVOICEABLE',

    // EPARGNES
    PENDING_SAVINGS = 'PENDING_SAVINGS',
    PROCESSABLE_SAVINGS = 'PROCESSABLE_SAVINGS',
    PAYABLE_SAVINGS_ORDERS = 'PAYABLE_SAVINGS_ORDERS',

    // MEDIATHEQUE
    MEDIA_OVERDUE = 'MEDIA_OVERDUE',
    MEDIA_DELIVRABLE = 'MEDIA_DELIVRABLE',

    // RESSOURCES PARTAGEES
    RESOURCE_OVERDUE = 'RESOURCE_OVERDUE',
    RESOURCE_DELIVRABLE = 'RESOURCE_DELIVRABLE',

}

export enum AccountingNotificationsTypes {
    UNSETTLED_SUPPLIER_INVOICE = 'UNSETTLED_SUPPLIER_INVOICE',
    UNSETTLED_CLIENT_INVOICE = 'UNSETTLED_CLIENT_INVOICE',
    UNLETTERED_MOVEMENTS = 'UNLETTERED_MOVEMENTS',
    BANK_STATEMENT_IN_PROGRESS = 'BANK_STATEMENT_IN_PROGRESS',
    ACCOUNTING_CLOSING_IN_PROGRESS = 'ACCOUNTING_CLOSING_IN_PROGRESS'
}

export type SubNotificationType =
    BeneficiaryNotificationsTypes
    | WebAdministrationNotificationsTypes
    | ActivityNotificationsTypes
    | AccountingNotificationsTypes;

export type ApplicationMessage = {
    icon: string,
    label: string,
    message: MessageAppliDto,
    level: number
};

export class HomeStateModel {

    selectedNotification: ListeHomeNotification = null;

    notificationsList: ListeHomeNotification[] = [];

    subNotificationType: SubNotificationType = null;

    applicationMessageList: ApplicationMessage[] = [];

    selectedApplicationMessage: ApplicationMessage = null;

}

@Injectable()
@State<HomeStateModel>({
    name: 'home',
    defaults: new HomeStateModel()
})
export class HomeState {

    constructor(private graphqlService: GraphqlService) {
    }

    @Selector()
    public static notificationsList(state: HomeStateModel): ListeHomeNotification[] {
        return state.notificationsList;
    }

    @Selector()
    public static subNotificationType(state: HomeStateModel): any {
        return state.subNotificationType;
    }

    @Selector()
    public static selectedNotification(state: HomeStateModel): ListeHomeNotification {
        return state.selectedNotification;
    }

    @Selector()
    public static applicationMessageList(state: HomeStateModel): ApplicationMessage[] {
        return state.applicationMessageList;
    }

    @Selector()
    public static selectedApplicationMessage(state: HomeStateModel): ApplicationMessage {
        return state.selectedApplicationMessage;
    }

    @Action(HomeSetNotificationAction)
    setNotification({patchState}: StateContext<HomeStateModel>, {notification}: HomeSetNotificationAction) {
        patchState({
            selectedNotification: notification
        });
    }

    @Action(HomeSetSubNotificationTypeAction)
    setSubNotificationType({patchState}: StateContext<HomeStateModel>, {subNotificationType}: HomeSetSubNotificationTypeAction) {
        patchState({
            subNotificationType
        });
    }

    @Action(HomeGetNotificationsListAction)
    getNotificationsList({patchState}: StateContext<HomeStateModel>) {
        return this.graphqlService.mutateOne<'GetListeHomeNotifications', MutationGetListeHomeNotificationsArgs>({
            mutation: gql`
                mutation GetListeHomeNotifications(
                    $contexte: ContextInfosInput,
                ) {
                    GetListeHomeNotifications(
                        contexte: $contexte
                    ) {
                        ListeHomeNotification {
                            Count
                            Informations {
                                ... on InformationsHomeNotification {
                                    Type
                                }
                                ... on InformationsActiviteHomeNotification {
                                    Type
                                    IdActivite
                                    LibelleActivite
                                    CodeTypeActivite
                                }
                            }
                            ListeNotifications {
                                BaseHomeNotification {
                                    ... on HomeNotification {
                                        Type
                                        Count
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            fetchPolicy: 'no-cache'
        })
        .pipe(
            tap(GetListeHomeNotifications => {
                const notificationsList = GetListeHomeNotifications?.ListeHomeNotification || [];
                patchState({notificationsList});
            }),
        );
    }

    @Action(HomeCloseNotificationAction)
    closeNotification(state: StateContext<HomeStateModel>) {
    }

    @Action(HomeGetApplicationMessagesListAction)
    getApplicationMessageList({patchState}: StateContext<HomeStateModel>) {

        const now = new Date().toISOString();
        return this.graphqlService.mutateOne<'GetListeMessageAppliGenerique', MutationGetListeMessageAppliGeneriqueArgs>({
            mutation: gql`
                mutation GetListeMessageAppliGenerique($contexte: ContextInfosInput, $criteres: CriteresMessageAppliDTOInput ) {
                    GetListeMessageAppliGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        MessageAppliDTO {
                            Id
                            OrdreImportance
                            DateDebutAffichage
                            DateFinAffichage
                            Message
                            MessageHTML
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    DateDebutMessage: now,
                    DateFinMessage: now,
                    CodeCibleLogiciel: 'UPGESTION'
                }
            },
            fetchPolicy: 'no-cache'
        }).pipe(
            tap(GetListeMessageAppliGenerique => {
                // fonction de récupération des secondes d'une date
                const second = (message: MessageAppliDto) => new Date(message.DateDebutAffichage).getTime();

                const applicationMessageList = GetListeMessageAppliGenerique?.MessageAppliDTO
                    // tri date desc
                    .sort((a, b) => second(b) - second(a))
                    // affichage
                    .map(message => ({
                        icon: 'notifications',
                        label: message.Message,
                        message,
                        level: message.OrdreImportance
                    })) || [];
                patchState({applicationMessageList});
            }),
        );
    }


    @Action(HomeSetApplicationMessageAction)
    setApplicationMessage({patchState}: StateContext<HomeStateModel>, {message}: HomeSetApplicationMessageAction) {
        patchState({
            selectedApplicationMessage: message
        });
    }

}
